import React, { useState, FormEvent, useEffect, useRef } from 'react';

const LOADING_PHRASES = [
  'Making 0s and 1s human-readable...',
  'Convincing the blockchain you\'re worth it...',
  'Minting your digital soul to the chain...',
  'Teaching validators your name...',
  'Bribing consensus nodes with good vibes...',
  'Asking Klever if you\'re real... please hold...',
  'Converting your vibe into cryptographic proof...',
  'Summoning your on-chain identity from the void...',
  'Adding you to the immutable hall of fame...',
  'Waiting for 67% of validators to agree you exist...',
  'Translating your personality into hexadecimal...',
  'Your profile is being forged in the fires of the blockchain...',
];
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Input, Button, LocationPicker } from '../../components';
import './CreateProfile.scss';
import { useAppDispatch } from '../../redux/Store';
import { createUserProfile, getUserProfile } from '../../redux/thunks/userProfileThunks';
import { fetchUserPreferences } from '../../redux/thunks/userPreferencesThunks';
import { updateUser } from '../../redux/slices/authSlice';
import { Location, getLocationById } from '../../redux/thunks/locationThunks';
import { getMediaUrl } from '../../constants/media';

/**
 * CreateProfile page component
 * First-time profile creation for new users
 * Also handles blockchain profile retry if user has basic profile but no blockchain profile
 */
const CreateProfile: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user, logout } = useAuth();

  // Escape hatch: if on-chain profile creation fails (e.g. the wallet
  // already has a profile from a prior signup on another env), the user
  // can otherwise get trapped here — ProfileGuard keeps routing them back
  // because hasBlockchainProfile=false. This lets them sign out so they
  // can pick a different wallet / account.
  const handleSignOut = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const [displayName, setDisplayName] = useState('');
  const [bio, setBio] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phraseIndex, setPhraseIndex] = useState(0);
  const phraseKey = useRef(0);
  const [homeLocation, setHomeLocation] = useState<Location | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

  // Conversion mode = post-cutover user re-minting on mainnet. Their
  // existing profile row survived the wipe (only the on-chain pointer
  // was severed), so we pre-fill the form with what we already know
  // instead of asking them to re-enter it. Distinguished from
  // first-time signup (nothing to prefill) and generic
  // blockchain-retry (rare, no cutover context).
  const isConversion = !!user?.needsMainnetMint;

  useEffect(() => {
    if (user?.hasProfile && !user?.hasBlockchainProfile) {
      console.log(isConversion ? '🔄 Mainnet conversion mode' : '🔄 Blockchain profile retry mode');
    }
  }, [user, isConversion]);

  // Prefill from existing profile + home location in conversion mode.
  // Runs once when the page mounts under needsMainnetMint. Failures
  // are non-fatal — the user can still fill fields manually if the
  // fetch hiccups.
  useEffect(() => {
    if (!isConversion) return;
    let cancelled = false;

    const prefill = async () => {
      try {
        const profileRes = await dispatch(getUserProfile()).unwrap();
        if (cancelled || !profileRes) return;
        if (profileRes.displayName) setDisplayName(profileRes.displayName);
        if (profileRes.bio) setBio(profileRes.bio);
        // avatarUrl is a URL (not a File) — resolve to an absolute
        // media URL (backend returns paths like /uploads/avatars/...
        // which won't load from the frontend origin) and show it as
        // the preview image. If the user doesn't upload a new file,
        // no upload happens and the backend's "profile already
        // exists" path leaves the stored avatarUrl untouched.
        if (profileRes.avatarUrl) {
          const resolved = getMediaUrl(profileRes.avatarUrl);
          if (resolved) setAvatarPreview(resolved);
        }
      } catch (err) {
        console.warn('Prefill: failed to fetch existing profile', err);
      }

      try {
        const prefs = await dispatch(fetchUserPreferences()).unwrap();
        if (cancelled || !prefs?.homeId) return;
        const loc = await dispatch(getLocationById(prefs.homeId)).unwrap();
        if (!cancelled && loc) setHomeLocation(loc);
      } catch (err) {
        console.warn('Prefill: failed to fetch home location', err);
      }
    };

    prefill();
    return () => { cancelled = true; };
  }, [isConversion, dispatch]);

  // Redirect away if the user already has both profiles — this page is only
  // meant for first-time setup, blockchain retry, or mainnet conversion.
  // needsMainnetMint takes precedence: even though hasBlockchainProfile
  // is false for wiped users (see AuthController), we double-guard here
  // in case that logic ever changes.
  if (user?.hasProfile && user?.hasBlockchainProfile && !user?.needsMainnetMint) {
    return <Navigate to="/dashboard" replace />;
  }

  // Cycle through loading phrases while submitting
  useEffect(() => {
    if (!isLoading) {
      setPhraseIndex(0);
      return;
    }
    const interval = setInterval(() => {
      phraseKey.current += 1;
      setPhraseIndex(i => (i + 1) % LOADING_PHRASES.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [isLoading]);

  // Handle avatar file selection
  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }

      setAvatarFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      setError(null);
    }
  };

  // Remove avatar
  const handleRemoveAvatar = () => {
    setAvatarFile(null);
    setAvatarPreview(null);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    // Validate required fields
    if (!homeLocation) {
      setError('Please select your home location');
      return;
    }

    setIsLoading(true);

    try {
      // Call thunk to create user profile
      const result = await dispatch(createUserProfile({
        displayName: displayName.trim() || undefined,
        bio: bio.trim() || undefined,
        homeLocationId: homeLocation.id,
        profileImage: avatarFile || undefined,
      }));

      if (result.meta.requestStatus === 'fulfilled') {
        const payload = result.payload as any;

        // Push avatarUrl into the auth slice so the AppBar reflects it
        // on the post-signup dashboard load. /auth/me doesn't return
        // avatarUrl, so without this the AppBar shows the default
        // avatar until a page refresh.
        const createdAvatarUrl = payload?.userProfile?.profile?.avatarUrl ?? undefined;
        dispatch(updateUser({
          hasProfile: true,
          ...(createdAvatarUrl ? { avatarUrl: createdAvatarUrl } : {}),
        }));

        // Check if blockchain profile creation failed
        if (payload.blockchainError) {
          console.warn('⚠️  Blockchain profile warning:', payload.blockchainError);
          // User profile created successfully, but blockchain failed
          setError(`Profile created, but blockchain setup failed: ${payload.blockchainError}`);
        } else if (payload.blockchainProfile) {
          console.log('✅ Full profile created (user + blockchain)');
          // Update user to mark blockchain profile as created
          dispatch(updateUser({ hasBlockchainProfile: true }));
          // Navigate to dashboard after successful profile creation
          navigate('/dashboard', { replace: true });
        } else {
          // Unexpected case - profile created but no blockchain response
          setError('Profile created, but blockchain status unknown. Please refresh the page.');
        }
      } else {
        // Handle rejection
        setError(result.payload as string || 'Failed to create profile');
      }
    } catch (err: any) {
      console.error('Profile creation failed:', err);
      setError('Failed to create profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="create-profile">
      <div className="create-profile-container">
        <div className="create-profile-header">
          <div className="header-icon">{isConversion ? '🔁' : '🎭'}</div>
          <h1>{isConversion ? 'Convert Your Profile to Mainnet' : 'Create Your Profile'}</h1>
          <p>
            {isConversion
              ? 'Re-mint your existing profile on the mainnet contract. Your details are pre-filled — review, adjust if needed, and confirm.'
              : 'Set up your blockchain identity on Klyx'}
          </p>
          <div className="progress-steps">
            <div className="step active">
              <div className="step-number">1</div>
              <div className="step-label">{isConversion ? 'Confirm Info' : 'Profile Info'}</div>
            </div>
            <div className="step-divider"></div>
            <div className="step">
              <div className="step-number">2</div>
              <div className="step-label">{isConversion ? 'Mint Mainnet' : 'Blockchain'}</div>
            </div>
            <div className="step-divider"></div>
            <div className="step">
              <div className="step-number">3</div>
              <div className="step-label">Complete</div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="create-profile-form">
          {/* Avatar Upload Section */}
          <div className="form-section avatar-section">
            <h2>
              <span className="section-icon">📸</span>
              Profile Picture
            </h2>
            <p className="section-description">
              {isConversion
                ? 'Your current profile picture. Upload a new one to change it, or leave as-is to keep the current one.'
                : 'Add a photo to personalize your profile (optional)'}
            </p>

            <div className="avatar-upload-container">
              <div className="avatar-preview">
                {avatarPreview ? (
                  <img src={avatarPreview} alt="Avatar preview" />
                ) : (
                  <div className="avatar-placeholder">
                    <span className="placeholder-icon">👤</span>
                  </div>
                )}
              </div>

              <div className="avatar-actions">
                <input
                  type="file"
                  id="avatar-input"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  disabled={isLoading}
                  className="hidden-input"
                />
                <label htmlFor="avatar-input" className="avatar-button upload">
                  <span className="button-icon">📁</span>
                  {avatarPreview ? 'Change Photo' : 'Upload Photo'}
                </label>
                {avatarPreview && (
                  <button
                    type="button"
                    onClick={handleRemoveAvatar}
                    disabled={isLoading}
                    className="avatar-button remove"
                  >
                    <span className="button-icon">🗑️</span>
                    Remove
                  </button>
                )}
              </div>
              <p className="avatar-hint">JPG, PNG or GIF. Max size 5MB.</p>
            </div>
          </div>

          {/* Basic Info Section */}
          <div className="form-section">
            <h2>
              <span className="section-icon">✨</span>
              Basic Information
            </h2>
            <p className="section-description">
              {isConversion
                ? 'Pre-filled from your existing profile. Edit if you want to change them for mainnet.'
                : 'Tell us a bit about yourself'}
            </p>

            <Input
              label="Display Name (Optional)"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={user?.username ? `Leave blank to use: ${user.username}` : 'Enter your display name'}
              disabled={isLoading}
              autoComplete="name"
              maxLength={30}
            />
            {!displayName.trim() && user?.username && (
              <p className="helper-text">
                <span className="helper-icon">💡</span>
                Your username <strong>{user.username}</strong> will be used as your display name
              </p>
            )}

            <div className="form-field">
              <label htmlFor="bio">
                Bio (Optional)
                <span className="label-hint">Share your story</span>
              </label>
              <textarea
                id="bio"
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="Tell us about yourself, your interests, or what brings you to Klyx..."
                disabled={isLoading}
                maxLength={500}
                rows={4}
                className="bio-textarea"
              />
              <div className="char-count">
                {bio.length}/500 characters
              </div>
            </div>
          </div>

          {/* Location Section */}
          <div className="form-section location-section">
            <h2>
              <span className="section-icon">📍</span>
              Home Location
              <span className="required-badge">Required</span>
            </h2>
            <p className="section-description">
              {isConversion
                ? 'Pre-filled from your existing profile. Search for a new city to change it.'
                : 'Your home location helps us personalize your experience and connect you with nearby users'}
            </p>

            <LocationPicker
              label=""
              placeholder="Search for your city or town..."
              value={homeLocation}
              onChange={setHomeLocation}
              disabled={isLoading}
              required
              helperText=""
            />

            {homeLocation && (
              <div className="location-selected">
                <span className="check-icon">✓</span>
                <span className="location-text">
                  {homeLocation.city}{homeLocation.admin1 ? `, ${homeLocation.admin1}` : ''}, {homeLocation.country || homeLocation.countryCode}
                </span>
              </div>
            )}
          </div>

          <div className="form-actions">
            <Button
              type="submit"
              variant="primary"
              size="large"
              fullWidth
              isLoading={isLoading}
              disabled={!homeLocation || isLoading}
            >
              {isLoading ? (
                <>
                  <span className="button-spinner">⏳</span>
                  {isConversion ? 'Minting on Mainnet...' : 'Creating Profile...'}
                </>
              ) : (
                <>
                  <span className="button-icon">{isConversion ? '🔁' : '🚀'}</span>
                  {isConversion ? 'Convert to Mainnet' : 'Create Profile'}
                </>
              )}
            </Button>

            {!homeLocation && (
              <p className="button-hint">
                Please select your home location to continue
              </p>
            )}

            {isLoading && (
              <p key={phraseKey.current} className="loading-phrase">
                {LOADING_PHRASES[phraseIndex]}
              </p>
            )}

            {error && (
              <div className="error-banner">
                <span className="error-icon">⚠️</span>
                {error}
              </div>
            )}

            {/* Escape hatch hidden in conversion mode — these users have
                a valid session, an existing profile, and a wiped on-chain
                pointer. Signing out doesn't help them; the correct
                recovery is to complete the mainnet mint. */}
            {!isConversion && (
              <button
                type="button"
                onClick={handleSignOut}
                disabled={isLoading}
                className="create-profile-signout"
              >
                Sign out and use a different wallet
              </button>
            )}
          </div>

          <div className="form-footer">
            <div className="blockchain-badge">
              <span className="badge-icon">🔗</span>
              <div className="badge-content">
                <strong>Blockchain Powered</strong>
                <p>
                  {isConversion
                    ? 'Your existing social graph, posts, and messages carry over. This mint creates a fresh mainnet NFT.'
                    : 'Your profile will be tokenized on the Klever blockchain'}
                </p>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateProfile;
